import React, { useContext } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { Link } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useLocation, useHistory } from 'react-router-dom';
import Paginator from '../../../../components/Paginator';
import { sortUrl, parse, toSortOrder } from '../../../../utils/UrlUtil';
import SurveyListHeader from './ExecutiveReportSurveyListHeader';
import { AccountContext } from '../../../../components/AccountContext';

const messages = defineMessages({
  managers: { id: 'exec_report_detail.managers', defaultMessage: 'Managers' },
  date: { id: 'exec_report_detail.date', defaultMessage: 'Date' },
  status: { id: 'attrs.status', defaultMessage: 'Status' },
});

const SURVEY_STATUS = {
  NEW: 'NEW',
  INVITED: 'INVITED',
  STARTED: 'STARTED',
  SUBMITTED: 'SUBMITTED',
  DECLINED: 'DECLINED',
  EXPIRED: 'EXPIRED',
};

const getSurveyStatus = (statuses) => {
  let totalAssessments = 0;
  Object.keys(SURVEY_STATUS).forEach((item) => {
    if (statuses[item]) {
      totalAssessments += parseInt(statuses[item], 10);
    }
  });
  const sumitted = statuses.SUBMITTED ? parseInt(statuses.SUBMITTED, 10) : 0;
  return {
    htmlString: `${sumitted} / ${totalAssessments}`,
    string: statuses.SUBMITTED === totalAssessments ? 'FINISH' : 'IN_PROGRESS',
  };
};

const surveyStatusStyleClass = (status) => {
  switch (status.string) {
    case 'FINISH': {
      return 'filter filter--success is-active';
    }
    case 'IN_PROGRESS': {
      return 'filter filter--warning is-active';
    }
    default:
      return '';
  }
};

const getSurveyStatusComp = (status) => {
  const styleClass = surveyStatusStyleClass(status);
  return <span className={styleClass}>{status.htmlString}</span>;
};

const nameTemplate = (rowData, { currentAccount, keyField, displayField }) => {
  const detailUrl = `/accounts/${currentAccount}/surveys/${rowData[keyField]}`;
  return (
    <Link to={detailUrl} className="app-link">
      {rowData[displayField]}
    </Link>
  );
};

const timeFieldTemplate = (rowData, { formatDate, field }) => {
  const updated = formatDate(moment(rowData[field]), {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
  return updated;
};

const SurveyList = ({ connection: { edges, totalCount, indexFrom, indexTo, pageInfo }, loading }) => {
  const location = useLocation();
  const history = useHistory();
  const currentAccount = useContext(AccountContext);
  const { formatMessage, formatDate } = useIntl();

  const onSortChange = (event) => {
    sortUrl(event, location, history);
  };
  // data list
  const listRows = edges.map(({ node }) => ({
    ...node,
    language: node.self ? node.self.respondent.locale : '',
    status: getSurveyStatusComp(getSurveyStatus(node.statuses)),
    // status: node.statuses,
  }));

  const params = parse(location.search);
  const { sort = {} } = params;
  const { sortBy, sortDir } = sort;

  // header
  const nameHeader = formatMessage(messages.managers);
  const dateHeader = formatMessage(messages.date);
  const statusHeader = formatMessage(messages.status);

  // footer
  const footer = <Paginator totalCount={totalCount} indexFrom={indexFrom} indexTo={indexTo} pageInfo={pageInfo} />;

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <SurveyListHeader />
      </div>
      <div className="p-col-12">
        <DataTable
          value={listRows}
          footer={footer}
          responsive
          sortField={sortBy}
          sortOrder={toSortOrder(sortDir)}
          onSort={onSortChange}
          lazy
          loading={loading}
        >
          <Column
            field="name"
            header={nameHeader}
            body={nameTemplate}
            keyField="id"
            displayField="name"
            currentAccount={currentAccount}
          />
          <Column field="status" header={statusHeader} />
          <Column field="createdOn" header={dateHeader} body={timeFieldTemplate} formatDate={formatDate} sortable />
        </DataTable>
      </div>
    </div>
  );
};

SurveyList.propTypes = {
  connection: PropTypes.objectOf(PropTypes.any).isRequired,
  loading: PropTypes.bool.isRequired,
};

export default SurveyList;
